import { createStore } from 'react-sweet-state';
import { produce } from 'immer';
import { cloneDeep, map } from 'lodash';
import isURL from 'validator/lib/isURL';

import { RESOURCE_CONTENT_TYPE } from 'src/data/performance';
import { waitForJob } from 'src/graphql/job';
import { bulkDeleteResources } from 'src/graphql/resource/bulkDeleteResources';
import { checkCustomerResourcesExistence } from 'src/graphql/resource/checkCustomerResourcesExistence';
import { createResourcesSync } from 'src/graphql/resource/createResourcesSync';
import { customerResourceFileAdd } from 'src/graphql/resource/customerResourceFileAdd';
import { customerResourceFileProcess } from 'src/graphql/resource/customerResourceFileProcess';
import { deleteResource } from 'src/graphql/resource/deleteResource';
import { extractResource } from 'src/graphql/resource/extractResource';
import { extractResourceContent } from 'src/graphql/resource/extractResourceContent';
import { extractResourceTitle } from 'src/graphql/resource/extractResourceTitle';
import { queryCustomerResource } from 'src/graphql/resource/queryCustomerResource';
import { queryCustomerResources } from 'src/graphql/resource/queryCustomerResources';
import { queryWorkspaceAssociatedResources } from 'src/graphql/resource/queryWorkspaceAssociatedResources';
import { updateResource } from 'src/graphql/resource/updateResource';
import { uploadFileToS3 } from 'src/lib/aws';
import { calculateFileMD5 } from 'src/lib/crypto';
import { BackendError } from 'src/lib/errors';
import { captureMessage } from 'src/lib/sentry';

import { waitForExtraction } from './utils';

const INITIAL_STATE = {
  customerResources: [],
  workspaceResources: [],

  loadingCustomerResources: false,
  loadingResources: false,
  resourcesFetched: false,

  isExtractingResourceTitle: false,
  isExtractingResourceContent: false,
  isCreatingResource: false,
  isUpdatingResource: false,
  isDeletingResource: false,
  isDeletingResourcesBulk: false,
};

export const PRIVATE_ACTIONS = {
  getResourcesByContentType:
    (contentType) =>
    ({ getState }) => {
      const { workspaceResources } = getState();
      return workspaceResources?.filter((resource) => resource.contentType === contentType) || [];
    },

  mergeResourceIntoStore:
    (resource) =>
    ({ getState, setState }) => {
      const { customerResources, workspaceResources } = getState();

      const producer = (draft) => {
        const itemExists = draft.some((t) => t.id === resource.id);
        if (itemExists) {
          draft.map((t) => (t.id === resource.id ? { ...t, ...resource } : t));
        } else {
          draft.push(resource);
        }
      };
      const newCustomerList = produce(customerResources, producer);
      const newWorkspaceList = produce(workspaceResources, producer);

      setState({
        customerResources: newCustomerList,
        workspaceResources: newWorkspaceList,
      });
    },

  removeResourcesFromStore:
    (resourceIds) =>
    ({ getState, setState }) => {
      const { customerResources, workspaceResources } = getState();

      const predicate = (r) => !resourceIds.includes(r.id);
      const newCustomerList = customerResources.filter(predicate);
      const newWorkspaceList = workspaceResources.filter(predicate);

      setState({
        workspaceResources: newWorkspaceList,
        customerResources: newCustomerList,
      });
    },

  checkResourcesExistence:
    (inputs) =>
    (_, { currentCustomerId, currentWorkspaceId }) => {
      return checkCustomerResourcesExistence({
        customerId: currentCustomerId,
        workspaceId: currentWorkspaceId,
        inputs,
      });
    },
};

/**
 * Public available actions
 */
export const ACTIONS = {
  init:
    () =>
    ({ dispatch }) => {
      dispatch(ACTIONS.clearStore());
    },

  clearStore:
    () =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },

  queryCustomerResources:
    () =>
    async ({ setState }, { currentCustomerId }) => {
      setState({ loadingCustomerResources: true });
      try {
        const customerResources = await queryCustomerResources({ customerId: currentCustomerId });
        setState({ customerResources });
      } finally {
        setState({ loadingCustomerResources: false });
      }
    },

  queryWorkspaceResources:
    (workspaceId, options) =>
    async ({ setState }, { currentCustomerId, currentWorkspaceId }) => {
      setState({ loadingResources: true });
      try {
        const resources = await queryWorkspaceAssociatedResources(
          {
            customerId: currentCustomerId,
            id: workspaceId || currentWorkspaceId,
          },
          options
        );

        if (!resources) {
          captureMessage('"queryWorkspaceAssociatedResources" GQL query returned empty data', {
            level: 'warning',
            extra: {
              source: 'ResourceStore -> queryWorkspaceResources',
            },
          });
          throw new Error('Customer invalid');
        }

        setState({ workspaceResources: resources });
      } finally {
        setState({
          loadingResources: false,
          resourcesFetched: true,
        });
      }
    },

  extractResourceTitle:
    (url) =>
    async ({ setState }, { currentCustomerId }) => {
      if (!isURL(url)) {
        return;
      }

      setState({ isExtractingResourceTitle: true });
      try {
        const result = await extractResourceTitle({
          customerId: currentCustomerId,
          input: { content: url, type: RESOURCE_CONTENT_TYPE.url },
        });

        const resource = await waitForExtraction({ id: result.id });
        return resource.title;
      } finally {
        setState({ isExtractingResourceTitle: false });
      }
    },

  extractResourceContent:
    ({ input, withImages }) =>
    async ({ setState }, { currentCustomerId, currentWorkspaceId }) => {
      setState({ isExtractingResourceContent: true });
      try {
        const result = await extractResourceContent({
          customerId: currentCustomerId,
          workspaceId: currentWorkspaceId,
          input,
          withImages,
        });

        const resource = await waitForExtraction({ id: result.id });
        return resource.html;
      } finally {
        setState({ isExtractingResourceContent: false });
      }
    },

  extractResource:
    ({ name, url, tags, file, contentType, checksum }) =>
    async ({ setState, dispatch }, { currentCustomerId, currentWorkspaceId }) => {
      // If this is a file resource then we should upload directly to s3
      if (file) {
        return dispatch(ACTIONS.uploadResource({ name, tags, file, contentType, checksum }));
      }

      // ... otherwise, we need to extract the resource from the URL
      setState({ isCreatingResource: true });
      try {
        const job = await extractResource({
          customerId: currentCustomerId,
          workspaceId: currentWorkspaceId,
          input: {
            content: url,
            contentType,
            name,
            tags,
          },
        });

        const extractionResult = await waitForExtraction({ id: job.id });

        const extractedResource = await queryCustomerResource({
          customerId: currentCustomerId,
          resourceId: extractionResult.resource_id,
        });

        if (extractedResource) {
          dispatch(PRIVATE_ACTIONS.mergeResourceIntoStore(extractedResource));
        }

        return extractedResource;
      } finally {
        setState({ isCreatingResource: false });
      }
    },

  createUrlResourcesSync:
    (resources) =>
    async ({ setState }, { currentCustomerId, currentWorkspaceId }) => {
      setState({ isCreatingResource: true });
      try {
        return createResourcesSync({
          customerId: currentCustomerId,
          workspaceId: currentWorkspaceId,
          resources,
        });
      } finally {
        setState({ isCreatingResource: false });
      }
    },

  uploadResource:
    ({ name, tags, file, contentType, checksum = null }) =>
    async ({ setState, dispatch }, { currentCustomerId, currentWorkspaceId }) => {
      setState({ isCreatingResource: true });
      try {
        // Calculate file checksum, it will be used by BE to check file existence (we might get it calculated already outside of this action)
        const localChecksum = checksum || (await calculateFileMD5(file));

        // Create resource record on BE, get pre-signed upload URL
        const addResult = await customerResourceFileAdd({
          customerId: currentCustomerId,
          workspaceId: currentWorkspaceId,
          name,
          tags,
          resourceFileType: contentType,
          fileInfo: {
            name: file.name,
            size: file.size,
            checksum: localChecksum,
          },
        });
        if (!addResult || !addResult.uploadUrl) {
          throw new BackendError('Failed to generate pre-signed URL for file upload');
        }

        // Push file to S3
        await uploadFileToS3({
          url: addResult.uploadUrl,
          file,
        });

        // Notify BE that file upload is complete and wait for BE to process the file
        const processingJob = await customerResourceFileProcess({
          customerId: currentCustomerId,
          resourceId: addResult.id,
        });
        await waitForJob(processingJob.id);

        // Fetch processed resource and display it to user
        const createdResource = await queryCustomerResource({
          customerId: currentCustomerId,
          resourceId: addResult.id,
        });
        if (createdResource) {
          dispatch(PRIVATE_ACTIONS.mergeResourceIntoStore(createdResource));
        }
        return createdResource;
      } finally {
        setState({ isCreatingResource: false });
      }
    },

  updateResource:
    ({ id, name, tags }) =>
    async ({ setState, dispatch }, { currentCustomerId, currentWorkspaceId }) => {
      setState({ isUpdatingResource: true });
      try {
        const updatedResource = await updateResource({
          id,
          customerId: currentCustomerId,
          workspaceId: currentWorkspaceId,
          input: { name, tags },
        });

        dispatch(PRIVATE_ACTIONS.mergeResourceIntoStore(updatedResource));

        return updatedResource;
      } finally {
        setState({ isUpdatingResource: false });
      }
    },

  deleteResource:
    (id) =>
    async ({ setState, dispatch }) => {
      setState({ isDeletingResource: true });
      try {
        const deletedResource = await deleteResource({ id });

        dispatch(PRIVATE_ACTIONS.removeResourcesFromStore([deletedResource.id]));
      } finally {
        setState({ isDeletingResource: false });
      }
    },

  deleteResourcesBulk:
    (ids) =>
    async ({ setState, dispatch }) => {
      setState({ isDeletingResourcesBulk: true });
      try {
        const deletedResources = await bulkDeleteResources({ ids });

        const deletedResourcesIds = map(deletedResources, 'id');
        dispatch(PRIVATE_ACTIONS.removeResourcesFromStore(deletedResourcesIds));
      } finally {
        setState({ isDeletingResourcesBulk: false });
      }
    },

  checkUrlResourcesExistence:
    (urls) =>
    ({ dispatch }) => {
      const inputs = urls?.map((url) => ({
        type: RESOURCE_CONTENT_TYPE.url,
        content: url,
      }));
      return dispatch(PRIVATE_ACTIONS.checkResourcesExistence(inputs));
    },

  checkFileResourceExistence:
    ({ type, checksum }) =>
    async ({ dispatch }) => {
      const inputs = [{ type, checksum }];
      const results = await dispatch(PRIVATE_ACTIONS.checkResourcesExistence(inputs));

      // We only need the first result, as we check just one file
      return results[0];
    },

  getWorkspaceResources:
    () =>
    ({ getState }) => {
      return getState().workspaceResources;
    },

  getWorkspaceResource:
    (id) =>
    ({ getState }) => {
      const { workspaceResources } = getState();
      return workspaceResources?.find((resource) => resource.id === id);
    },

  getDocumentResource:
    (id) =>
    ({ dispatch }) => {
      const resources = dispatch(
        PRIVATE_ACTIONS.getResourcesByContentType(RESOURCE_CONTENT_TYPE.document)
      );
      return resources.find((resource) => resource.id === id);
    },

  getImageResource:
    (id) =>
    ({ dispatch }) => {
      const resources = dispatch(
        PRIVATE_ACTIONS.getResourcesByContentType(RESOURCE_CONTENT_TYPE.image)
      );
      return resources.find((resource) => resource.id === id);
    },

  getUrlResources:
    () =>
    ({ dispatch }) => {
      return dispatch(PRIVATE_ACTIONS.getResourcesByContentType(RESOURCE_CONTENT_TYPE.url));
    },
};

export const ResourceStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'resource-store',
});
